import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Scheme interface
export interface Scheme {
  _id: string;
  name: string;
  grossTenure: number;
  advancedEMI: number;
  processingRate: number;
  forPartners: string[]; // When updating, use 'for' field with replace/add/remove operations
  lender: string[];      // When updating, use replace/add/remove operations
  category: string[];
  subCategory: string[];
  subventionCost?: number;
  interestBased?: boolean;
  annualInterest?: number;
  GST?: number;
  calOnRootAmt?: boolean;
  min?: number;
  serialNumber?: number; // Added by the frontend for display
  isActive?: boolean;
}

// Scheme detail response interface
export interface SchemeDetailResponse {
  status?: string;
  result: {
    data: {
      name: string;
      grossTenure: number;
      advancedEMI: number;
      subventionCost: number;
      interestBased: boolean;
      annualInterest: number;
      processingRate: number;
      GST: number;
      min: number;
      calOnRootAmt: boolean;
      partner: string[];
      lender: string[];
      category: string[];
      subCategory: string[];
      isActive: boolean;
    }
  }
}

// API response interface
export interface SchemeApiResponse {
  status: string;
  result: {
    data: {
      totalCount: number;
      totalMatch: number;
      totalPages: number;
      pageNo: number;
      pageSize: number;
      results: Scheme[];
    }
  }
}

// Filter type
export interface SchemeFilters {
  lender?: string;
  partner?: string;
}

// Fetch parameters interface
export interface FetchSchemesParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc' ;
  search?: string;
  token?: string | null;
  filters?: SchemeFilters;
  partner?: string;
}

/**
 * Fetch schemes with pagination, sorting, and search
 */
export const fetchSchemes = async ({
  page,
  pageSize,
  sortField,
  sortOrder,
  search,
  token,
  partner,
  filters = {}
}: FetchSchemesParams): Promise<{
  schemes: Scheme[];
  totalCount: number;
  totalMatch: number;
  totalPages: number;
  pageNo: number;
  pageSize: number;
}> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    // Add sort parameters if provided
    if (sortField && sortOrder) {
      params.append("sortBy", sortField);
      params.append("sortOrder", sortOrder);
    }

    // Add search parameter if provided
    if (search) {
      params.append("searchQuery", search);
    }

    // Add filter parameters
    if (filters.lender) {
      params.append("lender", filters.lender);
    }

    if (filters.partner) {
      params.append("partner", filters.partner);
    }

    if (partner) {
      params.append("partner", partner);
    }

    console.log(`[API] Fetching schemes with filters:`, {
      page,
      pageSize,
      sortField,
      sortOrder,
      search,
      filters
    });

    const url = `/adminschemes/fetch?${params.toString()}`;
    console.log(`[API] Fetching schemes with URL: ${url}`);

    const response = await axios.get<SchemeApiResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    // Check if response is valid
    if (response.data?.status !== "Request successful!" && response.data?.status !== "Created!" && response.data?.status !== "Updated!") {
      throw new Error("Unexpected API response structure");
    }

    const { totalCount, totalMatch, totalPages, pageNo, pageSize: returnedPageSize, results } = response.data.result.data;

    // Add serial numbers to schemes
    const schemesWithSerialNumbers = results.map((scheme, index) => ({
      ...scheme,
      serialNumber: (page - 1) * pageSize + index + 1
    }));

    return {
      schemes: schemesWithSerialNumbers,
      totalCount,
      totalMatch,
      totalPages,
      pageNo,
      pageSize: returnedPageSize
    };
  } catch (error: any) {
    console.error('Error fetching schemes:', error);
    showToast.error(error.message || 'Failed to fetch schemes');
    throw error;
  }
};

export const fetchBucketsByPartner = async (partner: string): Promise<string[]> => {
  try {
    const token = localStorage.getItem("token") || "";
    if (!token) throw new Error("No authentication token available");

    // Build query params
    const params = new URLSearchParams();
    params.append("partner", partner);

    const url = `/adminschemes/fetch?${params.toString()}`;
    console.log("[API] Fetch Buckets By Partner:", url);

    const response = await axios.get(url, {
      headers: { token }
    });

    const apiData = response.data?.result?.data;

    if (!Array.isArray(apiData)) {
      throw new Error("Invalid API response: expected array");
    }

    return apiData; // → ["TestBucket", "Gold", "Silver"]
  } catch (error: any) {
    console.error("Error fetching buckets by partner:", error);
    showToast.error(error.message || "Failed to fetch buckets");
    return [];
  }
};

export const fetchSchemesByBucketId = async (bucketId: string): Promise<any[]> => {
  try {
    const token = localStorage.getItem("token") || "";
    if (!token) throw new Error("No authentication token available");

    // Build query params
    const params = new URLSearchParams();
    params.append("bucketId", bucketId);

    const url = `/adminschemes/fetch?${params.toString()}`;
    console.log("[API] Fetch Schemes By BucketId:", url);

    const response = await axios.get(url, {
      headers: { token }
    });

    const apiData = response.data?.result?.data;

    if (!Array.isArray(apiData)) {
      throw new Error("Invalid API response: expected array of scheme docs");
    }

    return apiData; // → [{...scheme1}, {...scheme2}]
  } catch (error: any) {
    console.error("Error fetching schemes by bucketId:", error);
    showToast.error(error.message || "Failed to fetch schemes");
    return [];
  }
};


/**
 * Fetch scheme details by ID
 */
export const fetchSchemeDetails = async (
  schemeId: string,
  token?: string | null
): Promise<SchemeDetailResponse> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Check if scheme ID is provided
    if (!schemeId) {
      throw new Error("Scheme ID is required");
    }

    const url = `/adminschemes/details/${schemeId}`;
    console.log(`[API] Fetching scheme details with URL: ${url}`);

    const response = await axios.get<SchemeDetailResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    // Check if response is valid
    if (response.data?.status !== "Request successful!") {
      throw new Error("Unexpected API response structure");
    }

    return response.data;
  } catch (error: any) {
    console.error('Error fetching scheme details:', error);
    showToast.error(error.message || 'Failed to fetch scheme details');
    throw error;
  }
};

/**
 * Create a new scheme
 */
export const createScheme = async (
  schemeData: {
    name: string;
    grossTenure: number;
    advancedEMI: number;
    subventionCost?: number;
    interestBased?: boolean;
    annualInterest?: number;
    processingRate: number;
    GST?: number;
    calOnRootAmt?: boolean;
    min?: number;
    for: string[];
    lender: string[];
    category: string[];
    subCategory: string[];
    isActive?: boolean;
  },
  token?: string | null
) => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    const response = await axios.post(
      '/adminschemes/create',
      schemeData,
      {
        headers: {
          "Content-Type": "application/json",
          token: authToken,
        },
      }
    );

    // Check if response is valid
    if (response.data?.status !== "Created!") {
      throw new Error(response.data?.message || "Failed to create scheme");
    }

    showToast.success('Scheme created successfully');
    return response.data;
  } catch (error: any) {
    console.error('Error creating scheme:', error);
    showToast.error(error.message || 'Failed to create scheme');
    throw error;
  }
};

/**
 * Update an existing scheme
 */
export const updateScheme = async (
  schemeId: string,
  schemeData: {
    name?: string;
    grossTenure?: number;
    advancedEMI?: number;
    subventionCost?: number;
    interestBased?: boolean;
    annualInterest?: number;
    processingRate?: number;
    GST?: number;
    calOnRootAmt?: boolean;
    min?: number;
    for?: string[] | {
      replace?: string[];
      add?: string[];
      remove?: string[];
    };
    lender?: string[] | {
      replace?: string[];
      add?: string[];
      remove?: string[];
    };
    category?: string[] | {
      replace?: string[];
      add?: string[];
      remove?: string[];
    };
    subCategory?: string[] | {
      replace?: string[];
      add?: string[];
      remove?: string[];
    };
    isActive?: boolean;
  },
  token?: string | null
) => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Check if scheme ID is provided
    if (!schemeId) {
      throw new Error("Scheme ID is required");
    }

    const response = await axios.put(
      `/adminschemes/update/${schemeId}`,
      schemeData,
      {
        headers: {
          "Content-Type": "application/json",
          token: authToken,
        },
      }
    );

    // Check if response is valid
    if (response.data?.status !== "Request successful!" && response.data?.status !== "Updated!") {
      throw new Error(response.data?.message || "Failed to update scheme");
    }

    showToast.success('Scheme updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('Error updating scheme:', error);
    showToast.error(error.message || 'Failed to update scheme');
    throw error;
  }
};


export interface EmiCalcRequest {
  amount: number;
  partner: string;
  lender: string | string[];
  zypeVersion?: string;
  category?: string;
  subCategory?: string;
}

export interface EmiCalcResponse {
  status: string;
  message: string;
  data: any[];
}

export const calculateSchemes = async (
  payload: EmiCalcRequest
): Promise<any[]> => {
  try {
    const token = localStorage.getItem("localToken") || "";

    const response = await axios.post<EmiCalcResponse>(
      `/adminschemes/calculate`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          token,
        },
      }
    );

    if (response.data.status !== "success") {
      showToast?.error(response.data.message);
      return [];
    }

    return response.data.data || [];
  } catch (error: any) {
    console.error("Error calculating schemes:", error);

    const errorMessage =
      error.response?.data?.message || "Failed to calculate EMI schemes";

    showToast?.error(errorMessage);
    throw new Error(errorMessage);
  }
};